import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ButtonBase, Chip, Divider, Tooltip } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DirectionsIcon from "@mui/icons-material/Directions";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import moment from "moment";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { gpsStatusBadgeColor } from "utils/constant";
import { compassPoint, isLive, siteStatus } from "./linkedVehicleUtils";
import { vehicleColorKey } from "./LinkedVehicleMarker";

const chipSx = { height: 24, borderRadius: "6px", fontWeight: 700 };

const formatSpeed = (speed) => (speed == null ? "--" : `${Math.round(speed)} mph`);

function Section({ title, children }) {
  return (
    <MDBox mb={2}>
      <MDTypography variant="caption" color="text" fontWeight="bold" textTransform="uppercase">
        {title}
      </MDTypography>
      <MDBox mt={0.75} display="flex" flexDirection="column" gap={0.5}>
        {children}
      </MDBox>
    </MDBox>
  );
}

function Row({ label, value }) {
  return (
    <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={2}>
      <MDTypography variant="button" color="text" fontWeight="regular">
        {label}
      </MDTypography>
      <MDTypography variant="button" color="text" fontWeight="bold" textAlign="right">
        {value ?? "--"}
      </MDTypography>
    </MDBox>
  );
}

function VehicleList({ vehicles, selectedId, onSelect, site, now }) {
  return (
    <Section title={`Linked vehicles (${vehicles.length})`}>
      {vehicles.map((vehicle) => {
        const live = isLive(vehicle.lastUpdatedMs, now);
        const colorKey = vehicleColorKey(vehicle, live);
        const status = siteStatus(vehicle.position, site);
        const active = vehicle.id === selectedId;
        return (
          <ButtonBase
            key={vehicle.id}
            onClick={() => onSelect(vehicle.id)}
            aria-pressed={active}
            sx={({ palette, borders, functions: { rgba } }) => ({
              width: "100%",
              justifyContent: "flex-start",
              gap: 1.25,
              px: 1.25,
              py: 1,
              textAlign: "left",
              borderRadius: borders.borderRadius.md,
              border: `1px solid ${active ? palette.info.main : rgba(palette.text.main, 0.1)}`,
              backgroundColor: active ? rgba(palette.info.main, 0.12) : rgba(palette.text.main, 0.035),
            })}
          >
            <MDBox
              sx={({ palette }) => ({
                width: 10,
                height: 10,
                flexShrink: 0,
                borderRadius: "50%",
                backgroundColor: palette[colorKey].main,
              })}
            />
            <MDBox flex={1} minWidth={0}>
              <MDTypography variant="button" color="text" fontWeight="bold" display="block" noWrap>
                {vehicle.name}
              </MDTypography>
              <MDTypography variant="caption" color="text" display="block" noWrap>
                {[live ? vehicle.motionLabel : "Stale", formatSpeed(vehicle.speed), status?.label]
                  .filter(Boolean)
                  .join(" · ")}
              </MDTypography>
            </MDBox>
          </ButtonBase>
        );
      })}
    </Section>
  );
}

export default function LinkedVehicleDetailsPanel({ vehicles = [], selected, onSelect, site, now }) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  if (!selected) return null;

  const live = isLive(selected.lastUpdatedMs, now);
  const freshness = selected.lastUpdatedMs ? moment(selected.lastUpdatedMs).from(moment(now)) : "No data yet";
  const status = siteStatus(selected.position, site);
  const { lat, lng } = selected.position;
  const coordinateText = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;

  const handleCopy = async () => {
    if (!navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(coordinateText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      console.error("copy coordinates", error);
    }
  };

  const handleOpenMaps = () => {
    const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <MDBox
      width={{ xs: "100%", md: 340 }}
      flexShrink={0}
      p={2.5}
      sx={({ palette, functions: { rgba } }) => ({
        overflowY: "auto",
        borderLeft: { md: `1px solid ${rgba(palette.text.main, 0.12)}` },
        borderTop: { xs: `1px solid ${rgba(palette.text.main, 0.12)}`, md: "none" },
      })}
    >
      {vehicles.length > 1 && (
        <VehicleList vehicles={vehicles} selectedId={selected.id} onSelect={onSelect} site={site} now={now} />
      )}

      <MDBox display="flex" flexWrap="wrap" gap={1} mb={2}>
        <Chip size="small" sx={chipSx} label={live ? "LIVE" : "STALE"} color={live ? "success" : "error"} />
        <Chip size="small" sx={chipSx} label={selected.motionLabel} color={gpsStatusBadgeColor(selected.motionLabel)} />
        {status && (
          <Chip size="small" sx={chipSx} label={status.label} color={status.onSite ? "success" : "default"} />
        )}
      </MDBox>

      <Section title="Vehicle">
        <Row label="Name" value={selected.name} />
        <Row label="Device ID" value={selected.id} />
      </Section>

      <Section title="Status">
        <Row label="Last update" value={freshness} />
        <Row label="Speed" value={formatSpeed(selected.speed)} />
        <Row label="Heading" value={`${Math.round(selected.heading)}° ${compassPoint(selected.heading)}`} />
        {selected.locationLabel && (
          <MDTypography variant="caption" color="text">
            {selected.locationLabel}
          </MDTypography>
        )}
      </Section>

      {site && (
        <Section title="Gateway site">
          <Row label="Name" value={site.name} />
          {site.address && (
            <MDTypography variant="caption" color="text">
              {site.address}
            </MDTypography>
          )}
          {site.source !== "address" && (
            <MDTypography variant="caption" color="text" sx={{ opacity: 0.7 }}>
              {site.source === "setup" ? "Using Device Setup coordinates" : "Using gateway-reported coordinates"}
            </MDTypography>
          )}
        </Section>
      )}

      <Divider sx={{ my: 2 }} />

      <Section title="Location">
        <Row label="Coordinates" value={coordinateText} />
        <MDBox display="flex" flexWrap="wrap" gap={1} mt={1}>
          <Tooltip title={copied ? "Copied" : "Copy coordinates"}>
            <span>
              <MDButton size="small" variant="outlined" color="info" startIcon={<ContentCopyIcon />} onClick={handleCopy}>
                {copied ? "Copied" : "Copy"}
              </MDButton>
            </span>
          </Tooltip>
          <MDButton size="small" variant="gradient" color="info" startIcon={<DirectionsIcon />} onClick={handleOpenMaps}>
            Google Maps
          </MDButton>
          <MDButton
            size="small"
            variant="outlined"
            color="info"
            startIcon={<OpenInNewIcon />}
            onClick={() => navigate(`/devices/view-device/${selected.id}`)}
          >
            Open vehicle
          </MDButton>
        </MDBox>
      </Section>
    </MDBox>
  );
}
